import { config } from "../config";
import type { Quality } from "../types/quality";

/**
 * Photographie des capacités de la machine, prise une fois au démarrage.
 * Ne s'abonne à rien : ce qui bouge (taille, orientation) est géré par Stage.
 */
export class Device {
  readonly hasTouch: boolean;
  readonly isCoarsePointer: boolean;
  readonly isMobile: boolean;
  readonly pixelRatio: number;
  readonly cores: number;
  readonly gpu: string | null;
  readonly tier: Quality;

  constructor() {
    this.hasTouch = navigator.maxTouchPoints > 0 || "ontouchstart" in window;
    this.isCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
    // Un écran tactile de portable reste un desktop : il faut les deux signaux.
    this.isMobile = this.hasTouch && this.isCoarsePointer;
    this.pixelRatio = Math.min(
      window.devicePixelRatio || 1,
      this.isMobile
        ? config.device.maxPixelRatioMobile
        : config.device.maxPixelRatioDesktop,
    );
    this.cores = navigator.hardwareConcurrency || 2;
    this.gpu = readGpuName();
    this.tier = this.computeTier();
  }

  /**
   * Heuristique volontairement grossière : elle donne un point de départ,
   * pas un verdict. À remplacer par une mesure de fps réelle le jour où
   * les scènes sont assez lourdes pour que ça se voie.
   */
  private computeTier(): Quality {
    if (this.gpu === null) return "low";
    if (this.isMobile) return this.cores >= 6 ? "medium" : "low";
    if (/swiftshader|llvmpipe|software/i.test(this.gpu)) return "low";
    if (this.cores >= 8) return "high";
    return this.cores >= 4 ? "medium" : "low";
  }
}

/**
 * Nom du GPU via WEBGL_debug_renderer_info. L'extension est masquée par
 * certains navigateurs ; l'absence de nom vaut « je ne sais pas », pas « faible ».
 */
const readGpuName = (): string | null => {
  const canvas = document.createElement("canvas");
  try {
    const gl = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
    if (!gl) return null;
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    const name = info
      ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return typeof name === "string" ? name : null;
  } catch {
    return null;
  }
};
